import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const EmailVerificationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');

  const verifyEmail = useCallback(async (token) => {
    try {
      const response = await axios.post(`${API}/auth/verify-email?token=${token}`);
      setStatus('success');
      setMessage('Your email has been verified successfully!');
      toast.success('Email verified!');
      setTimeout(() => navigate('/login'), 3000);
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Verification failed. The link may have expired.');
      toast.error('Verification failed');
    }
  }, [navigate]);

  useEffect(() => {
    if (token) {
      verifyEmail(token);
    } else {
      setStatus('error');
      setMessage('No verification token provided');
    }
  }, [token, verifyEmail]);

  return (
    <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center px-6">
      <div className="w-full max-w-md text-center">
        <div className="bg-white/60 backdrop-blur-md border border-[#EAE4DC] shadow-lg rounded-2xl p-12">
          {status === 'verifying' && (
            <>
              <Loader2 className="w-16 h-16 text-stone-600 mx-auto mb-6 animate-spin" />
              <h2
                className="text-2xl font-medium mb-4"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Verifying Your Email
              </h2>
              <p className="text-stone-600">Please wait...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-6" />
              <h2
                className="text-2xl font-medium mb-4 text-green-700"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Email Verified!
              </h2>
              <p className="text-stone-600 mb-6">{message}</p>
              <p className="text-sm text-stone-500">Redirecting to login...</p>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="w-16 h-16 text-red-600 mx-auto mb-6" />
              <h2
                className="text-2xl font-medium mb-4 text-red-700"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Verification Failed
              </h2>
              <p className="text-stone-600 mb-6">{message}</p>
              <Button
                onClick={() => navigate('/login')}
                className="rounded-full px-8 bg-stone-800 text-stone-50 hover:bg-stone-700"
              >
                Go to Login
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
